/** Progressive delivery integration: feature flags, dynamic config, and metric events. */

import { useEffect, useState } from "react";
import { z } from "zod";
import * as Sentry from "@sentry/react";
import { StatsigClient } from "@statsig/js-client";
import { usePolling } from "../hooks/usePolling";
import { useLocalStorageState } from "../hooks/useLocalStorageState";

export interface FeatureUser {
  userId?: string;
  email?: string;
  custom?: Record<string, string | number | boolean>;
}

/** Options for logMetricEvent. All fields are optional. */
export interface MetricEventOptions {
  user?: FeatureUser;
  metadata?: Record<string, unknown>;
  /** Numeric value for sum/mean/percentile aggregation in Statsig. Omit for pure occurrence counting. */
  value?: number;
}

let _client: StatsigClient | null = null;
let _lastUserId: string | undefined = undefined;
let _initPromise: Promise<unknown> | null = null;

/** Converts a FeatureUser to the Statsig user shape. */
function toStatsigUser(user?: FeatureUser) {
  return {
    userID: user?.userId ?? "anonymous",
    email: user?.email,
    custom: user?.custom,
  };
}

// Read once at module load — available from the first event.
const _environment =
  (import.meta.env.VITE_ENVIRONMENT as string | undefined) ?? import.meta.env.MODE ?? "unknown";

/**
 * Stringifies metadata values for the Statsig event API (requires Record<string, string>)
 * and injects `environment` so every event is breakable by environment without extra config.
 */
function toStatsigMetadata(metadata?: Record<string, unknown>): Record<string, string> {
  return Object.fromEntries(
    Object.entries({ environment: _environment, ...metadata }).map(([k, v]) => [k, String(v)])
  );
}

/**
 * Sets the Statsig user (call on session load/change; pass `undefined` for logged-out → anonymous).
 * Fires a non-blocking user update only when the resolved userID actually changes, so repeat calls
 * with the same user are free. Gate/config checks return the previous user's cached result until the
 * async update completes; subsequent reads reflect the new user.
 */
export function setFeatureUser(user?: FeatureUser): void {
  if (!_client) return;
  const resolved = toStatsigUser(user);
  if (resolved.userID === _lastUserId) return;
  _lastUserId = resolved.userID;
  _client.updateUserAsync(resolved).catch(() => {});
}

/**
 * Initialises the Statsig browser client once. Reads VITE_STATSIG_CLIENT_KEY.
 * No-op when the key is absent. Sync-returning — flag checks return false while the
 * async SDK handshake is in flight (SDK is offline-safe by design).
 */
export function initializeProgressiveDelivery(): void {
  const key = import.meta.env.VITE_STATSIG_CLIENT_KEY as string | undefined;
  if (!key) return;

  const environment =
    (import.meta.env.VITE_ENVIRONMENT as string | undefined) ?? import.meta.env.MODE;

  // Initialise with the anonymous user (a userID of "anonymous") rather than an empty object, so
  // configs targeted by User ID evaluate cleanly for logged-out visitors instead of warning. Once
  // a session loads, UserProvider calls setFeatureUser() to switch to the real user.
  const anonymous = toStatsigUser();
  _lastUserId = anonymous.userID;
  _client = new StatsigClient(key, anonymous, { environment: { tier: environment } });
  _initPromise = _client.initializeAsync().catch(() => {
    // intentional no-op — all gate checks degrade to false while offline
  });
}

/** Returns true when the feature flag is enabled. Returns false when uninitialised. */
export function isFeatureFlagEnabled(flag: string, user?: FeatureUser): boolean {
  if (!_client) return false;
  if (user) setFeatureUser(user);
  return _client.checkGate(flag);
}

/** Parses a raw dynamic config value through its schema, reporting a mismatch to Sentry. */
function parseDynamicConfig<T>(name: string, schema: z.ZodType<T>, raw: unknown): T | null {
  const result = schema.safeParse(raw);
  if (!result.success) {
    Sentry.captureException(new Error(`Dynamic config "${name}" failed validation`), {
      extra: { issues: result.error.issues },
      tags: { "config.name": name },
    });
    return null;
  }
  return result.data;
}

/** Returns the named dynamic config's raw JSON value, or undefined while uninitialised/mid-handshake. */
function getRawDynamicConfig(name: string): unknown {
  if (!_client || _client.loadingStatus !== "Ready") return undefined;
  return _client.getDynamicConfig(name).value;
}

/**
 * Parses the named Statsig dynamic config through a Zod schema.
 * Returns null when uninitialised or still mid-handshake — an in-flight client returns empty
 * placeholder values that would otherwise parse successfully against an all-defaults schema and
 * wrongly overwrite the real cached config. Reports validation failures to Sentry and returns null.
 */
export function getDynamicConfig<T>(name: string, schema: z.ZodType<T>): T | null {
  const raw = getRawDynamicConfig(name);
  if (raw === undefined) return null;
  return parseDynamicConfig(name, schema, raw);
}

/** Fires a custom metric event. Fire-and-forget. */
export function logMetricEvent(event: string, options?: MetricEventOptions): void {
  if (!_client) return;
  _client.logEvent(event, options?.value, toStatsigMetadata(options?.metadata));
}

/**
 * Polls the named Statsig dynamic config every `pollMs` milliseconds, caching the raw value in
 * localStorage for stale-while-revalidate on the next page load, and re-parsing it through the
 * schema on every read. Caching the raw value (rather than the schema's parsed output) matters
 * for schemas that coerce into non-JSON-native types (e.g. `z.coerce.date()`): `JSON.stringify`
 * flattens a `Date` back into a string, and a stored parsed object would never get re-coerced on
 * the next load. Returns null until the first value is available (cache miss and Statsig not yet
 * initialised).
 */
export function useDynamicConfig<T>(name: string, schema: z.ZodType<T>, pollMs: number): T | null {
  const [raw, setRaw] = useLocalStorageState<unknown>(`rc_dynamic_config_${name}`);

  usePolling(async () => {
    // Wait out the initial handshake so the first poll doesn't race it, get a placeholder
    // "uninitialised" result, and then wait a full pollMs before trying again.
    await (_initPromise ?? Promise.resolve());
    const value = getRawDynamicConfig(name);
    if (value !== undefined) setRaw(value);
  }, pollMs);

  return raw === null ? null : parseDynamicConfig(name, schema, raw);
}

/**
 * React hook returning whether a feature flag is enabled for the given user.
 * Re-evaluates when the flag name or any field of the user changes. Returns false while
 * uninitialised.
 */
export function useFeatureFlag(flag: string, user?: FeatureUser): boolean {
  const [enabled, setEnabled] = useState(() => isFeatureFlagEnabled(flag, user));
  const { userId, email, custom } = user ?? {};

  useEffect(() => {
    setEnabled(isFeatureFlagEnabled(flag, { userId, email, custom }));
  }, [flag, userId, email, custom]);

  return enabled;
}
